import {
  type AgentTechnology,
  type EnforcementCoverage,
  type EstateAgentMcpService,
  type PolicyRequirement,
  type PolicyTargets,
  policyToolKey,
} from "@/api/temp/AgentEstateService";
import type { GeneratedAgent } from "./generate";
import {
  getIntentPolicies,
  getRecommendationCatalog,
  POLICY_CATALOG,
  POLICY_REQUIREMENTS,
  type RecommendationCatalogEntry,
} from "./seed";

/**
 * Where a policy acts on one agent, resolved from that agent's own MCP
 * servers, tools and data sources - never from anything the agent does not
 * reach. Mock stand-in for the policy engine's target resolution.
 */
export interface AgentPolicyTargets extends Omit<PolicyTargets, "policyId"> {
  applicable: boolean;
  /** Data controls per data source, so unions never double-count one source. */
  controlsBySource: Record<string, number>;
  requirement?: PolicyRequirement;
  reason?: string;
  /**
   * The agent's MCP servers and tools could not be read, so whether the policy
   * reaches it is unknown - which is not the same as it not applying.
   */
  unresolved?: boolean;
}

export function matchesSelector(
  agent: GeneratedAgent,
  entry: RecommendationCatalogEntry,
): boolean {
  // Named agents decide on their own: an Intent Policy spans several agents,
  // and matching them by scenario alone would confine it to one.
  if (entry.selector.agentKeys)
    return !!agent.demoKey && entry.selector.agentKeys.includes(agent.demoKey);
  if (agent.demoScenario || entry.demoScenario)
    return (
      agent.demoScenario === entry.demoScenario &&
      entry.selector.purposes.includes(agent.purpose)
    );
  return (
    entry.selector.purposes.includes(agent.purpose) &&
    entry.selector.technologies.includes(agent.connectedResource)
  );
}

const EMPTY: Omit<AgentPolicyTargets, "applicable"> = {
  mcpServiceIds: [],
  toolKeys: [],
  dataSourceIds: [],
  dataControlCount: 0,
  controlsBySource: {},
};

function targetsOf(
  services: EstateAgentMcpService[],
  controlsFor: (sourceId: string) => number = () => 0,
  allowedSources?: Set<string>,
): Omit<AgentPolicyTargets, "applicable"> {
  const sources = new Set<string>();
  const toolKeys: string[] = [];
  const serviceIds: string[] = [];
  services.forEach(service => {
    const tools = allowedSources
      ? service.tools.filter(
          tool => !!tool.dataSourceId && allowedSources.has(tool.dataSourceId),
        )
      : service.tools;
    if (!tools.length) return;
    serviceIds.push(service.id);
    tools.forEach(tool => {
      toolKeys.push(policyToolKey(service.id, tool.id));
      if (tool.dataSourceId) sources.add(tool.dataSourceId);
    });
  });
  const controlsBySource = Object.fromEntries(
    [...sources].map(sourceId => [sourceId, controlsFor(sourceId)]),
  );
  return {
    mcpServiceIds: serviceIds,
    toolKeys,
    dataSourceIds: [...sources],
    dataControlCount: Object.values(controlsBySource).reduce(
      (sum, count) => sum + count,
      0,
    ),
    controlsBySource,
  };
}

const byTechnology = (
  agent: GeneratedAgent,
  services: EstateAgentMcpService[],
  technologies: Array<AgentTechnology | "Any">,
) => {
  if (technologies.includes("Any")) return services;
  const matched = services.filter(service =>
    technologies.includes(service.technology),
  );
  // A generated agent fronts its connected resource through one server whose
  // catalog technology can differ; the resource itself is what matched.
  if (!matched.length && technologies.includes(agent.connectedResource))
    return services;
  return matched;
};

/**
 * One policy on one agent. `technology` covers catalog entries and drafts;
 * recommendations and Intent Policies carry their own rules.
 */
export function resolvePolicyTargets(
  policyId: string,
  agent: GeneratedAgent,
  services: EstateAgentMcpService[],
  draftTechnology?: AgentTechnology | "Any",
): AgentPolicyTargets {
  const intent = getIntentPolicies().find(item => item.id === policyId);
  const recommendation = getRecommendationCatalog().find(
    item => item.id === policyId,
  );

  if (recommendation && !matchesSelector(agent, recommendation))
    return {
      ...EMPTY,
      applicable: false,
      reason: "The selected agents are outside this policy's purpose and resources.",
    };

  // With no readable servers there is nothing to resolve targets against.
  // That is an unknown, not a "not applicable": the agent is kept out of both
  // the targeted and the excluded sets rather than guessed into one.
  if (services.length === 0)
    return {
      ...EMPTY,
      applicable: false,
      unresolved: true,
      reason:
        "This agent's MCP servers and tools could not be read, so where the policy applies is unknown.",
    };

  if (intent) {
    const reached = services.filter(service => !!intent.mcp[service.id]);
    const targets = targetsOf(
      reached,
      sourceId => intent.data[sourceId]?.controlIds.length ?? 0,
    );
    // A data source the policy has no mapping for is reached, but not governed
    // by this policy - it stays out of the policy's scope.
    const mapped = targets.dataSourceIds.filter(id => !!intent.data[id]);
    return {
      ...targets,
      dataSourceIds: mapped,
      controlsBySource: Object.fromEntries(
        mapped.map(id => [id, targets.controlsBySource[id]]),
      ),
      applicable: targets.mcpServiceIds.length > 0,
    };
  }

  const technologies: Array<AgentTechnology | "Any"> = recommendation
    ? recommendation.selector.technologies
    : [
        POLICY_CATALOG.find(item => item.id === policyId)?.technology ??
          draftTechnology ??
          "Any",
      ];
  const candidates = byTechnology(agent, services, technologies);
  const rule = POLICY_REQUIREMENTS[policyId];
  const targets = targetsOf(
    candidates,
    undefined,
    rule ? new Set(rule.dataSourceIds) : undefined,
  );
  if (targets.mcpServiceIds.length) return { ...targets, applicable: true };

  if (rule)
    return {
      ...EMPTY,
      applicable: false,
      requirement: { required: rule.required, detected: "None" },
      reason: rule.reason,
    };
  const reached = [...new Set(services.map(service => service.technology))];
  return {
    ...EMPTY,
    applicable: false,
    requirement: {
      required: `${technologies.join(", ")} resources`,
      detected: reached.length ? reached.join(", ") : "None",
    },
    reason:
      "The resources this policy governs are not present in the selected agents' current access path.",
  };
}

/** Distinct servers, tools and data sources across several target sets. */
export function unionCoverage(
  targets: Array<Omit<AgentPolicyTargets, "applicable">>,
): EnforcementCoverage {
  const services = new Set<string>();
  const tools = new Set<string>();
  const controls = new Map<string, number>();
  targets.forEach(target => {
    target.mcpServiceIds.forEach(id => services.add(id));
    target.toolKeys.forEach(id => tools.add(id));
    Object.entries(target.controlsBySource).forEach(([id, count]) =>
      controls.set(id, Math.max(controls.get(id) ?? 0, count)),
    );
    target.dataSourceIds.forEach(id => {
      if (!controls.has(id)) controls.set(id, 0);
    });
  });
  return {
    mcpServerCount: services.size,
    toolCount: tools.size,
    dataSourceCount: controls.size,
    controlCount: [...controls.values()].reduce((sum, count) => sum + count, 0),
  };
}
